// Copy-helper för auto-genererade city×hub-sidor.
// Tar en CityMeta och bygger LandingPage-props i Fennys röst,
// med stadens branscher, neighbors och edge inflätat i copyn.
//
// Används av nya videoproduktion- och employer-branding-rutter där vi
// vill hålla samma kvalitet som hand-skrivna Stockholm/Göteborg/Malmö-pages
// men slippa duplicera 21 filer per hub.

import type { ReactNode } from "react";
import type { LandingProps, LandingFAQ } from "@/components/site/landing/LandingPage";
import type { CityMeta } from "./cities";
import { buildRelatedHubs, type HubKind } from "./related-hubs";
import { presenceFor, locationSentence } from "./presence";

/** Liten utility för att rendera ankarlänkar i copy. */
function L({ href, children }: { href: string; children: ReactNode }) {
  return (
    <a
      className="underline decoration-mint/40 underline-offset-4 hover:text-mint"
      href={href}
    >
      {children}
    </a>
  );
}

/** Snyggt komma-separerat tre-i-rad: A, B och C. */
function joinList(items: string[], max = 3): string {
  const slice = items.slice(0, max);
  if (slice.length <= 1) return slice.join("");
  return slice.slice(0, -1).join(", ") + " och " + slice.at(-1);
}

/* -------------------------------------------------------------------------- */
/* VIDEOPRODUKTION                                                            */
/* -------------------------------------------------------------------------- */

export interface CityHubPage {
  /** Properties som skickas till LandingPage */
  props: LandingProps;
  /** Metadata som routen behöver för head() */
  meta: {
    title: string;
    description: string;
    ogTitle: string;
    ogDescription: string;
    path: string;
    schemaName: string;
    breadcrumbLabel: string;
    breadcrumbHubLabel: string;
    breadcrumbHubPath: string;
  };
  faq: LandingFAQ[];
}

export function videoproduktionForCity(city: CityMeta): CityHubPage {
  const industries = joinList(city.industries);
  const neighbors = joinList(city.neighbors);
  const path = `/videoproduktion/${city.slug}`;

  const faq: LandingFAQ[] = [
    {
      q: `Vad är skillnaden mellan videoproduktion och filmproduktion i ${city.name}?`,
      a: "Begreppen används ofta synonymt. Vi använder \"videoproduktion\" om kortare, kanalspecifika format (sociala medier, webb, intern kommunikation) och \"filmproduktion\" om större kampanjer och brand-film. Samma team, samma kvalitet — oavsett vad ni kallar det.",
    },
    {
      q: `Vad kostar videoproduktion i ${city.name}?`,
      a: "Enklare videoproduktion (1 dag, 1 video + sociala versioner): 25–60 kkr. Större kampanj med flera dagars inspelning: 80–500 kkr. Vill ni ha löpande output finns Pulse-abonnemang från fast månadsbudget.",
    },
    {
      q: `Vilka videoformat producerar ni för kunder i ${city.regionLong}?`,
      a: `Sociala medier-video, produktvideo, kundcase, employer branding, eventvideo, webinarklipp, internkommunikation, explainer och animerad video. Vi anpassar formatet efter ${industries} — det är där tonläget skiljer sig.`,
    },
    {
      q: `Reser ni från ${city.name} till ${neighbors}?`,
      a: `Ja — vi rullar produktioner i hela ${city.regionLong}. Resa inom upptagningsområdet ingår och vi planerar logistiken så ni inte betalar för dödtid.`,
    },
  ];

  const props: LandingProps = {
    eyebrow: `Videoproduktion · ${city.name}`,
    title: <>Videoproduktion i {city.name} — för flödet, inte för arkivet.</>,
    lede: `Fenny är en videobyrå med produktionsteam för ${city.name} och ${city.regionLong}. Vi gör sociala medier-video, kundcase, produktvideo, employer branding och internkommunikation — i en process där strategi, manus, foto, klipp och versionering hänger ihop. Pulse-abonnemang för löpande output eller engångsproduktion.`,
    tldr: (
      <p>
        Fenny producerar video i <strong>{city.name}</strong> — strategi, regi,
        foto, klipp och versionering inhouse. Pulse-abonnemang från{" "}
        <strong>14 995 kr/mån</strong> för löpande videoflöde eller enskilda
        Prime-produktioner till fastpris. {city.edge}
      </p>
    ),
    ctaPrimary: { label: `Boka möte`, to: "/kontakt" },
    ctaSecondary: { label: "Se case", to: "/case" },
    trackingId: `videoproduktion-${city.slug}`,
    bullets: [
      {
        title: "Kanalanpassat",
        body: "Vi börjar med kanalen — LinkedIn, Reels, TikTok, sajt eller intranät — och bygger formatet därifrån. Inte tvärtom.",
      },
      {
        title: "Volym i takt",
        body: "Pulse levererar 4–20 färdiga videor per månad. Slipp engångsproduktioner som tar slut på två veckor.",
      },
      {
        title: presenceFor(city.slug, city.name).label,
        body: `${presenceFor(city.slug, city.name).line} Vi reser vidare till ${neighbors} utan extra logistikkostnad.`,
      },
    ],
    sections: [
      {
        id: "format",
        heading: "Vilka videoformat producerar vi?",
        body: (
          <p>
            <L href="/sociala-medier-film">Sociala medier-video</L>,{" "}
            <L href="/produktfilm">produktvideo</L>, kundcase,{" "}
            <L href="/employer-branding-film">employer branding-film</L>,
            eventvideo, internkommunikation,{" "}
            <L href="/animerad-reklamfilm">animerad video</L> och explainer.
            Vi sätter ihop paketen efter er kanalmix — inte efter en lista.
          </p>
        ),
      },
      {
        id: "vem",
        heading: `Vem jobbar vi med i ${city.name}?`,
        body: (
          <p>
            Vi har särskild vana av {industries} i {city.regionLong} — från
            first-clip till löpande månadsleverans. {city.edge}
          </p>
        ),
      },
      {
        id: "andra-vinklar",
        heading: `Andra vinklar för ${city.name}`,
        body: (
          <p>
            Letar ni efter en bredare ingång? Se{" "}
            <L href={`/filmproduktion/${city.slug}`}>
              filmproduktion i {city.name}
            </L>
            ,{" "}
            <L href={`/reklamfilm/${city.slug}`}>reklamfilm i {city.name}</L>{" "}
            eller{" "}
            <L href={`/employer-branding-film/${city.slug}`}>
              employer branding-film i {city.name}
            </L>
            . Allt kommer från samma team.
          </p>
        ),
      },
    ],
    faq,
    caseLimit: 3,
    closing: {
      title: `Sätter vi upp ett möte i ${city.name}?`,
      body: "30 minuter där vi mappar er videoinsats mot tre konkreta nästa steg.",
      primary: { label: "Boka möte", to: "/kontakt" },
      secondary: { label: "Räkna pris", to: "/priser" },
    },
    related: buildRelatedHubs(city, "videoproduktion" as HubKind),
  };

  return {
    props,
    faq,
    meta: {
      title: `Videoproduktion ${city.name}: pris och process | Fenny`,
      description: `Videoproduktion i ${city.name}: sociala medier-video, kundcase och produktvideo för ${industries}. Inspelning på plats i ${locationSentence(presenceFor(city.slug, city.name), 2)}, första klippet på 10–14 dagar.`,
      ogTitle: `Videoproduktion ${city.name} — Fenny`,
      ogDescription: `Videobyrå i ${city.name} med hela kedjan inhouse — strategi, regi, foto, klipp.`,
      path,
      schemaName: `Fenny — videobyrå i ${city.name}. Sociala medier-video, kundcase, produktvideo och employer branding.`,
      breadcrumbLabel: city.name,
      breadcrumbHubLabel: "Videoproduktion",
      breadcrumbHubPath: "/videoproduktion",
    },
  };
}

/* -------------------------------------------------------------------------- */
/* EMPLOYER BRANDING-FILM                                                     */
/* -------------------------------------------------------------------------- */

export function employerBrandingForCity(city: CityMeta): CityHubPage {
  const industries = joinList(city.industries);
  const neighbors = joinList(city.neighbors);
  const path = `/employer-branding-film/${city.slug}`;

  const faq: LandingFAQ[] = [
    {
      q: `Vad är employer branding-film, och varför gör vi det i ${city.name}?`,
      a: `Film som bygger ert arbetsgivarvarumärke — för att attrahera rätt sökanden, sänka rekryteringskostnaden och stärka stoltheten internt. I ${city.name} jobbar vi särskilt med ${industries} där talangkonkurrensen är hård och kandidater jämför arbetsgivare innan de söker.`,
    },
    {
      q: `Vad kostar employer branding-film i ${city.name}?`,
      a: "Brand-anchor (huvudfilm 60–120 sek): 90–250 kkr. Medarbetar-porträtt: 25–60 kkr/st, billigare som serie. Komplett paket (1 anchor + 5 porträtt): 250–450 kkr. Pulse-abonnemang för löpande employer-content från fast månadsbudget.",
    },
    {
      q: `Får ni medarbetare på era ${city.name}-produktioner att kännas naturliga framför kameran?`,
      a: "Ja. Vi förintervjuar 20 min innan, regisserar samtal istället för att läsa manus, och ger gott om tid på set. Resultatet låter som riktiga människor — för att det är riktiga människor.",
    },
    {
      q: `Filmar ni på kontor i ${city.name} och ${neighbors}?`,
      a: `Ja — vi kommer ut till ert kontor eller produktion. Hela ${city.regionLong} ligger inom vårt upptagningsområde och resekostnad inom regionen ingår. För större brand-anchor använder vi ofta studio eller hyrd location.`,
    },
  ];

  const props: LandingProps = {
    eyebrow: `Employer branding · ${city.name}`,
    title: (
      <>
        Employer branding-film i {city.name} som faktiskt påverkar
        rekryteringskostnaden.
      </>
    ),
    lede: `Fenny producerar employer branding-film för arbetsgivare i ${city.name} och ${city.regionLong}. Anchor-film, medarbetar-porträtt och kulturfilm i regi som låter äkta — för att den är det. Resultat: fler rätt sökanden, färre fel.`,
    tldr: (
      <p>
        Employer branding-film i <strong>{city.name}</strong>: typiskt en
        90–180 sek brandfilm + 4–6 medarbetarporträtt för LinkedIn och
        karriärsajt. Pris: <strong>60 000–200 000 kr</strong> per produktion
        eller löpande via <strong>Pulse från 14 995 kr/mån</strong>. Vi rullar
        produktioner i hela {city.regionLong}.
      </p>
    ),
    ctaPrimary: { label: "Boka strategimöte", to: "/kontakt" },
    ctaSecondary: {
      label: "Rekryteringsfilm",
      to: "/rekryteringsfilm",
    },
    trackingId: `employer-branding-${city.slug}`,
    bullets: [
      {
        title: "Skiljer från rekrytering",
        body: "Employer branding bygger varumärket. Rekryteringsfilm säljer en specifik tjänst. Ni behöver båda — och vi gör båda.",
      },
      {
        title: "Serier sänker styckpris",
        body: "Fem porträtt från samma uppsättning blir 40 % billigare per styck än enstaka produktioner.",
      },
      {
        title: `Lokalt i ${city.region}`,
        body: `${presenceFor(city.slug, city.name).line} Vi reser till kontor och produktion i ${neighbors} utan extra logistikkostnad.`,
      },
    ],
    sections: [
      {
        id: "vad-vi-gor",
        heading: `Vad vi producerar för arbetsgivare i ${city.name}`,
        body: (
          <p>
            Brand-anchor (90–180 sek hero), medarbetar-porträtt i serie,
            kulturfilm, dokumentärt material från riktiga arbetsdagar,
            <L href="/rekryteringsfilm"> rekryteringsfilm</L> för specifika
            tjänster samt onboarding-paket. Allt levereras versionerat för{" "}
            <L href="/film-for-linkedin">LinkedIn</L>, karriärsajt och paid
            social.
          </p>
        ),
      },
      {
        id: "vem",
        heading: `Vem vi jobbar med i ${city.name}`,
        body: (
          <p>
            Bolag inom {industries} där talangkonkurrensen är hård. {city.edge}{" "}
            Vi är vana vid att producera känsligt material — sekretess, GDPR
            och samtycken från medarbetare hanteras i processen, inte i
            efterhand.
          </p>
        ),
      },
      {
        id: "andra-vinklar",
        heading: `Andra filmtjänster i ${city.name}`,
        body: (
          <p>
            Bredare ingång?{" "}
            <L href={`/filmproduktion/${city.slug}`}>
              Filmproduktion i {city.name}
            </L>{" "}
            samlar allt vi gör.{" "}
            <L href={`/reklamfilm/${city.slug}`}>Reklamfilm i {city.name}</L>{" "}
            för kampanj-arbete, eller{" "}
            <L href={`/videoproduktion/${city.slug}`}>
              videoproduktion i {city.name}
            </L>{" "}
            för löpande sociala medier-flöde.
          </p>
        ),
      },
    ],
    faq,
    caseLimit: 3,
    closing: {
      title: `Bygger ni arbetsgivarvarumärket i ${city.name}?`,
      body: "30 minuter där vi mappar er employer branding mot konkreta tre nästa steg.",
      primary: { label: "Boka möte", to: "/kontakt" },
      secondary: { label: "Räkna pris", to: "/priser" },
    },
    related: buildRelatedHubs(city, "employer-branding-film" as HubKind),
  };

  return {
    props,
    faq,
    meta: {
      title: `Employer branding-film ${city.name}: så gör vi | Fenny`,
      description: `Employer branding-film i ${city.name} för ${industries}: brand anchors, medarbetar-porträtt och kulturfilm. Inspelning på plats i ${locationSentence(presenceFor(city.slug, city.name), 2)}, klipp på 10–14 dagar.`,
      ogTitle: `Employer branding-film ${city.name} — Fenny`,
      ogDescription: `Film som bygger arbetsgivarvarumärket i ${city.name}.`,
      path,
      schemaName: `Fenny — employer branding-film i ${city.name}. Anchor-film, medarbetarporträtt och kulturfilm för ${industries}.`,
      breadcrumbLabel: city.name,
      breadcrumbHubLabel: "Employer branding-film",
      breadcrumbHubPath: "/employer-branding-film",
    },
  };
}
